import { LabelWidthValues } from '../../../types/common/types';
import { WEB_COMPONENT_GZ_DROPDOWN_MENU } from '../../../webcomponents.constants';
import { html, TemplateResult, PropertyValues, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { GzDropdownMenuWebComponent } from '../../gzDropdownMenu';
import { GzSingleSelectListWebComponent } from '../../gzSingleSelectList';
import { ListItem, SelectedOption } from '../../gzSingleSelectList/types';
import { styles } from '../view/styles';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { DropdownMenuEvents } from '../../gzDropdownMenu/types';
import { SingleSelectFilterType } from '../../gzSingleSelectComplex/types';

@customElement('gz-single-select')
export class GzSingleSelectWebComponent extends FormFieldInput<string | number> {
  @property({ type: String, attribute: true })
  label = '';

  @property({ type: String, attribute: true, reflect: true })
  labelWidth = LabelWidthValues.MEDIUM;

  @property({ type: Array, attribute: true, reflect: true })
  filterOptions: ListItem[] = [];

  @property({ attribute: true })
  filterContext: any = null;

  @property({ type: String, attribute: true, reflect: true })
  searchPlaceholderString = '';

  @property({ type: String, attribute: true, reflect: true })
  noSearchResultsString = '';

  @property({ type: String, attribute: true, reflect: true })
  placeholder = '';

  @property({ type: Boolean, reflect: true })
  disabled = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  isLoading = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  isError = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  readonly = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  autoAdjustMenuPosition = false;

  @property({ type: String, attribute: true, reflect: true })
  value: string | number = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  invalid = false;

  // By default, this selector is meant to be used in forms
  @property({ type: Boolean, attribute: true, reflect: true })
  isFilter = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  cannotDelete = false;

  @property({ type: SingleSelectFilterType, attribute: true, reflect: true })
  filterType = SingleSelectFilterType.NONE;

  @property({ type: Boolean, attribute: true, reflect: true })
  shouldAlignVertically = false;

  @property({ type: Number, attribute: true, reflect: true })
  verticalAlignThreshold = 0.35;

  @property({ type: Boolean, attribute: true, reflect: true })
  shouldHighlightSearchStrings = false;

  /**
   * If set to TRUE, the single select list component will only emit a `searchInputChange` event containing the
   * search string instead of performing client side list filtering. This can be used when the single select is used
   * for server-side filtering.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  hasDynamicSearch = false;

  @query(WEB_COMPONENT_GZ_DROPDOWN_MENU)
  dropdownMenu!: GzDropdownMenuWebComponent;

  @query('#single-selection-list')
  protected readonly selectList!: GzSingleSelectListWebComponent;

  static get styles() {
    return styles;
  }

  protected getSelectedItem(): ListItem | undefined {
    return this.filterOptions.find(option => option.value === this.value);
  }

  protected getSelectedItemRenderer(): TemplateResult | undefined {
    const selectedItem = this.getSelectedItem();
    return selectedItem && selectedItem.itemSuffixRenderer ? selectedItem.itemSuffixRenderer(selectedItem) : undefined;
  }

  render() {
    return html`
      <gz-dropdown-menu
        ?canDelete="${!this.readonly && !this.cannotDelete}"
        label="${this.label}"
        labelWidth="${this.labelWidth}"
        @open="${this.handleDropdownOpen}"
        @close="${this.handleDropdownClose}"
        @clear="${this.handleClearFilter}"
        placeholder="${this.placeholder}"
        ?isError="${this.isError || this.invalid}"
        errorTooltipText="${this.errorTooltipText}"
        ?readonly="${this.readonly}"
        ?autoAdjustMenuPosition="${this.autoAdjustMenuPosition}"
        ?shouldAlignVertically="${this.shouldAlignVertically}"
        .verticalAlignThreshold="${this.verticalAlignThreshold}"
        .selectedValuesStrings="${this.value}"
      >
        ${this.renderDropdownMenuContent()}
        <span slot="label-suffix"> ${this.value !== '' ? this.getSelectedItemRenderer() : nothing} </span>
      </gz-dropdown-menu>
    `;
  }

  updated(properties: PropertyValues): void {
    if (properties.has('value')) {
      this.setFormValue(this.value);
    }
  }

  protected renderDropdownMenuContent(): TemplateResult {
    return html` <div class="filter--content" id="content">${this.renderSelectList()}</div> `;
  }

  protected renderSelectList(): TemplateResult {
    return html` <gz-single-select-list
      id="single-selection-list"
      .filterOptions="${this.filterOptions}"
      searchPlaceholderString="${this.searchPlaceholderString}"
      noSearchResultsString="${this.noSearchResultsString}"
      @selectionChange="${this.handleListSelectionChange}"
      ?hasDynamicSearch="${this.hasDynamicSearch}"
      ?readonly="${this.readonly}"
      ?isLoading="${this.isLoading}"
      ?shouldHighlightSearchStrings="${this.shouldHighlightSearchStrings}"
      class="single-select-list"
      part="list"
    ></<gz-single-select-list>`;
  }

  protected handleListSelectionChange(e: CustomEvent): void {
    this.filterOptions = this.selectList.filterOptions;
    this.value = e.detail.selectedValue;
    const selectedOption = this.selectList.selectedOption;
    this.updateDropdownLabelWithSelectedValues(selectedOption);

    if (this.dropdownMenu.open) {
      this.dropdownMenu.open = false;
      this.dropdownMenu.dispatchEvent(new CustomEvent(DropdownMenuEvents.CLOSE));
    }
  }

  protected updateDropdownLabelWithSelectedValues(selectedOption: SelectedOption | null): void {
    /**
     * Don't display the selected values if the label width is small. See 'More' column in the design file
     * https://xd.adobe.com/view/c35ce011-09c5-43f1-6c2a-106f66a46de0-b56d/screen/a9ced8a4-e3b8-4d11-b65b-5eae4a51db50/specs/
     */
    if (
      !selectedOption ||
      this.dropdownMenu.labelWidth === LabelWidthValues.SMALL ||
      this.dropdownMenu.labelWidth === LabelWidthValues.AUTO
    ) {
      this.dropdownMenu.label = this.label;
      this.dropdownMenu.selectedValuesStrings = '';
    } else {
      this.dropdownMenu.selectedValuesStrings = selectedOption.label;
    }
  }

  protected handleDropdownOpen(e: MouseEvent): void {
    if (this.isLoading || this.isError) {
      return;
    }

    this.selectList.updateComplete.then(() => {
      if (this.filterOptions.length <= 10) {
        this.selectList.setListScrollPosition(0);
      }
    });
    this.dispatchEvent(new CustomEvent('open', { bubbles: true, composed: true }));
    this.selectList.selectionEventEmitted = false;
  }

  protected handleDropdownClose(e: Event): void {
    if (!this.isError && !this.isLoading) {
      this.selectList.clearSearchInput(e);
      this.selectList.filterOptions = [...this.filterOptions];
      this.dispatchEvent(new Event('blur'));
      this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
    }
  }

  protected handleClearFilter() {
    this.updateDropdownLabelWithSelectedValues(null);
    this.selectList.selectedOption = null;

    this.selectList.selectionEventEmitted = false;
    this.selectList.emitConfirmSelectionEvent();
    this.selectList.selectionEventEmitted = true;
  }

  /**
   * Function used to programatically set a value for this field
   * @param value
   */
  public setFormValue(value: string | number) {
    const optionExists: SelectedOption[] = this.filterOptions.filter(option => option.value === value);
    let selectedOption: SelectedOption | null = null;

    this.value = value;

    if (!isEmpty(optionExists)) {
      selectedOption = optionExists[0];
    }

    this.updateDropdownLabelWithSelectedValues(selectedOption);
    this.selectList.selectedOption = selectedOption;

    /*
     If this filter isn't used in forms, we don't want to emit the selection change event when we set it programmatically
     because we might mistake this event for a user manually changing the value.
     If the listener on this event isn't a validator, like on forms, but instead tries to re-renders this webcomponent,
     we might get stuck in an infinite loop
   */
    if (!this.isFilter && !this.selectList.selectionEventEmitted) {
      this.selectList.emitConfirmSelectionEvent();
    }
  }

  public getFormValue(): string | number {
    return this.value;
  }
}
